// File: src/components/contact/ContactForm.js

import React, { useState, useRef } from 'react';
import styles from './ContactForm.module.css';
import ScrollAnimation from '../common/ScrollAnimation';
import { CONTACT_TYPES, SOCIAL_LINKS } from '../../config/constants';
import { cn, isValidEmail } from '../../utils/helpers';
import apiService from '../../services/apiService';
import { Turnstile } from '@marsidev/react-turnstile';

function ContactForm() {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    company: '',
    type: '',
    message: '',
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const turnstileRef = useRef(null);
  const [turnstileToken, setTurnstileToken] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.first_name.trim()) newErrors.first_name = 'First name is required.';
    if (!formData.last_name.trim()) newErrors.last_name = 'Last name is required.';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required.';
    } else if (!isValidEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email.';
    }
    if (!formData.type) newErrors.type = 'Please select an inquiry type.';
    if (!formData.message.trim()) newErrors.message = 'Message is required.';
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!turnstileToken) {
      setErrors({ submit: 'Security check not complete. Please wait a moment and try again.' });
      return;
    }

    const newErrors = validate();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsSubmitting(true);
    setErrors({});

    try {
      const response = await apiService.submitContactForm({
        first_name: formData.first_name.trim(),
        last_name: formData.last_name.trim(),
        email_address: formData.email.trim().toLowerCase(),
        company: formData.company.trim() || null,
        inquiry_type: formData.type,
        message: formData.message.trim(),
        language: 'en-ca',
        user_agent: navigator.userAgent,
        turnstile_token: turnstileToken,
      });

      if (response.success) {
        setSubmitted(true);
        setTurnstileToken(null);
      } else {
        setErrors({
          submit: response.error || 'Failed to send your message. Please try again.'
        });
      }
    } catch (error) {
      console.error('[ContactForm] Submit error:', error);
      setErrors({
        submit: 'Something went wrong. Please try again or email us directly.'
      });
      turnstileRef.current?.reset();
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    setSubmitted(false);
    setFormData({
      first_name: '',
      last_name: '',
      email: '',
      company: '',
      type: '',
      message: ''
    });
    setErrors({});
  };

  // ─── Success State ────────────────────────────────────────────────────────────

  if (submitted) {
    return (
      <section className={cn('section', styles.section)}>
        <div className="site-container">
          <ScrollAnimation>
            <div className={styles.successCard}>
              <div className={styles.successIcon}>✓</div>
              <h2 className={styles.successHeading}>Thank You!</h2>
              <p className={styles.successText}>
                We've received your message and will get back to you shortly.
              </p>
              <button
                className="btn btn-primary btn-medium"
                onClick={handleReset}
              >
                Send Another Message
              </button>
            </div>
          </ScrollAnimation>
        </div>
      </section>
    );
  }

  // ─── Form ─────────────────────────────────────────────────────────────────────

  return (
    <section className={cn('section', styles.section)}>
      <div className="site-container">
        <div className={styles.layout}>

          {/* Left info */}
          <ScrollAnimation>
            <div className={styles.info}>
              <span className={styles.badge}>Get in Touch</span>
              <h2 className={styles.heading}>Let's Talk</h2>
              <p className={styles.description}>
                Whether you're looking for information, exploring a partnership,
                or want to stay updated on our progress — we'd love to hear from you.
              </p>

              <div className={styles.contactMethods}>
                <div className={styles.method}>
                  <h4 className={styles.methodTitle}>Request for Info</h4>
                  <p className={styles.methodDesc}>
                    Learn more about our technology and how it can work for you.
                  </p>
                </div>
                <div className={styles.method}>
                  <h4 className={styles.methodTitle}>Become a Partner</h4>
                  <p className={styles.methodDesc}>
                    Explore collaboration opportunities with GreenScience.
                  </p>
                </div>
              </div>

              {SOCIAL_LINKS.linkedin && (
                <a
                  href={SOCIAL_LINKS.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.socialLink}
                >
                  Connect on LinkedIn ↗
                </a>
              )}

              {/* Phone number */}
              <div className={styles.phoneNumbers}>
                <a href="tel:+18005138098" className={styles.phoneLink}>
                  <span className={styles.phoneLabel}>Toll-Free</span>
                  <span className={styles.phoneNumber}>1 (800) 513-8098</span>
                </a>
              </div>
            </div>
          </ScrollAnimation>

          {/* Right form */}
          <ScrollAnimation delay={200}>
            <form onSubmit={handleSubmit} className={styles.form} noValidate>

              {/* First + Last name row */}
              <div className={styles.formRow}>
                <div className={styles.field}>
                  <label htmlFor="contact-first-name" className="label">
                    First Name *
                  </label>
                  <input
                    id="contact-first-name"
                    name="first_name"
                    type="text"
                    value={formData.first_name}
                    onChange={handleChange}
                    className={cn('input', errors.first_name && 'input-error')}
                    placeholder="Jane"
                    disabled={isSubmitting}
                  />
                  {errors.first_name && (
                    <p className={styles.error}>{errors.first_name}</p>
                  )}
                </div>
                <div className={styles.field}>
                  <label htmlFor="contact-last-name" className="label">
                    Last Name *
                  </label>
                  <input
                    id="contact-last-name"
                    name="last_name"
                    type="text"
                    value={formData.last_name}
                    onChange={handleChange}
                    className={cn('input', errors.last_name && 'input-error')}
                    placeholder="Smith"
                    disabled={isSubmitting}
                  />
                  {errors.last_name && (
                    <p className={styles.error}>{errors.last_name}</p>
                  )}
                </div>
              </div>

              {/* Email + Company row */}
              <div className={styles.formRow}>
                <div className={styles.field}>
                  <label htmlFor="contact-email" className="label">
                    Email *
                  </label>
                  <input
                    id="contact-email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={cn('input', errors.email && 'input-error')}
                    placeholder="you@company.com"
                    disabled={isSubmitting}
                  />
                  {errors.email && (
                    <p className={styles.error}>{errors.email}</p>
                  )}
                </div>
                <div className={styles.field}>
                  <label htmlFor="contact-company" className="label">
                    Company
                  </label>
                  <input
                    id="contact-company"
                    name="company"
                    type="text"
                    value={formData.company}
                    onChange={handleChange}
                    className="input"
                    placeholder="Your company"
                    disabled={isSubmitting}
                  />
                </div>
              </div>

              {/* Inquiry type — full width */}
              <div className={styles.field}>
                <label htmlFor="contact-type" className="label">
                  Inquiry Type *
                </label>
                <select
                  id="contact-type"
                  name="type"
                  value={formData.type}
                  onChange={handleChange}
                  className={cn('input', errors.type && 'input-error')}
                  disabled={isSubmitting}
                >
                  <option value="">Select...</option>
                  {CONTACT_TYPES.map((ct) => (
                    <option key={ct.id} value={ct.id}>{ct.label}</option>
                  ))}
                </select>
                {errors.type && (
                  <p className={styles.error}>{errors.type}</p>
                )}
              </div>

              {/* Message */}
              <div className={styles.field}>
                <label htmlFor="contact-message" className="label">
                  Message *
                </label>
                <textarea
                  id="contact-message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  className={cn('input textarea', errors.message && 'input-error')}
                  placeholder="Tell us how we can help..."
                  rows={5}
                  disabled={isSubmitting}
                />
                {errors.message && (
                  <p className={styles.error}>{errors.message}</p>
                )}
              </div>

              {/* API-level error */}
              {errors.submit && (
                <div className={styles.submitError}>
                  {errors.submit}
                </div>
              )}

              <Turnstile
                ref={turnstileRef}
                siteKey={process.env.REACT_APP_TURNSTILE_SITE_KEY}
                onSuccess={(token) => setTurnstileToken(token)}
                onExpire={() => setTurnstileToken(null)}
                onError={() => setTurnstileToken(null)}
                options={{ theme: 'auto' }}
              />

              <button
                type="submit"
                className={cn('btn btn-primary btn-large', styles.submitBtn)}
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <span className={styles.spinner} />
                    Sending...
                  </>
                ) : (
                  'Send Message'
                )}
              </button>

            </form>
          </ScrollAnimation>
        </div>
      </div>
    </section>
  );
}

export default ContactForm;